import { useTheme } from '@mui/joy';
import { ReactNode } from 'react';
import { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { UpsertAddressFormFields } from 'addresses/components';
import { DEFAULT_REQUIRED_FIELDS } from 'addresses/constants';
import { AddressFields } from 'addresses/enums';
import { IAddressForm } from 'addresses/types';
import { ContactGroupType } from 'contacts/enums';
import { contactGroupService } from 'contacts/services';
import { IContactGroup } from 'contacts/types';
import { Form, FormField } from 'core/components';
import { FormSectionHeader } from 'core/elements';
import { InputType } from 'core/enums';
import { useFormContext } from 'core/states';
import { Size, Variant } from 'theme/enums';

interface IProps {
  isOpen?: boolean;
  onClose?: (reset?: UseFormReset<IAddressForm>) => void;
  onSubmit?: (reset: UseFormReset<IAddressForm>) => (data: IAddressForm) => void;
  contactGroup?: Partial<IContactGroup>;
  contactGroupType: ContactGroupType;
  isEdit?: boolean;
  level?: number;
  withoutForm?: boolean;
  extraButtons?: ReactNode | ReactNode[];
  requiredAddressFields?: AddressFields[];
}

export const UpsertLocationFormFields = ({
  isOpen,
  onClose,
  onSubmit,
  contactGroup,
  contactGroupType,
  isEdit,
  level,
  withoutForm = false,
  extraButtons,
  requiredAddressFields = DEFAULT_REQUIRED_FIELDS,
}: IProps) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const { control, errors, watch } = useFormContext();

  const isShipToValue = watch('isShipTo');
  const isBillToValue = watch('isBillTo');

  const disabledBillTo = isEdit;

  const switchSxProps = (isChecked: boolean) => ({
    sx: {
      '--Switch-trackBackground': isChecked
        ? `${theme.palette.primary.softBg} !important`
        : theme.palette.neutral[400],
    },
    labelSx: {
      color: theme.palette.neutral[isChecked ? 800 : 600],
    },
  });

  const fieldStack = (
    <>
      <FormField
        name="name"
        type={InputType.TEXT_INPUT}
        inputProps={{
          control,
          error: errors?.name,
          placeholder: t('labelLocationName'),
          label: t('labelLocationName'),
          required: true,
        }}
      />

      <FormField
        name="contactGroup"
        type={InputType.AUTOCOMPLETE}
        inputProps={{
          control,
          error: errors?.contactGroup,
          label: t('labelCompany'),
          required: true,
          getOptions: contactGroupService.getManyAutocomplete,
          requestPayload: {
            filters: {
              ...(contactGroupType === ContactGroupType.CUSTOMER && {
                isCustomer: '"1"',
              }),
              ...(contactGroupType === ContactGroupType.VENDOR && {
                isVendor: '"1"',
              }),
            },
          },
        }}
        hidden={!!contactGroup}
      />

      <FormSectionHeader title={t('labelAddress')} />

      <UpsertAddressFormFields
        requiredFields={requiredAddressFields}
        hiddenFields={[AddressFields.NAME]}
      />

      {contactGroupType === ContactGroupType.CUSTOMER && (
        <>
          <FormField
            name="customerNumber"
            type={InputType.TEXT_INPUT}
            inputProps={{
              control,
              error: errors?.customerNumber,
              placeholder: t('labelCustomerNumber'),
              label: t('labelCustomerNumber'),
            }}
          />

          <FormField
            name="isShipTo"
            type={InputType.SWITCH}
            inputProps={{
              control,
              error: errors?.isShipTo,
              label: t('labelShipTo'),
              size: Size.MD,
              variant: isShipToValue ? Variant.SOFT : Variant.SOLID,
              ...switchSxProps(isShipToValue),
            }}
          />

          <FormField
            name="isBillTo"
            type={InputType.SWITCH}
            inputProps={{
              control,
              error: errors?.isBillTo,
              label: t('labelBillTo'),
              size: Size.MD,
              variant: isBillToValue ? Variant.SOFT : Variant.SOLID,
              disabled: disabledBillTo,
              ...switchSxProps(isBillToValue),
            }}
          />
        </>
      )}
    </>
  );

  if (withoutForm) {
    return fieldStack;
  }

  return (
    <Form
      title={isEdit ? t('titleEditLocation') : t('titleAddLocation')}
      subtitle={contactGroup?.name}
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={onSubmit}
      level={level}
      submitBtnLabel={isEdit ? t('buttonLabelUpdateLocation') : t('buttonLabelCreateLocation')}
      cancelBtnLabel={t('buttonLabelCancel')}
      extraButtons={extraButtons}
    >
      {fieldStack}
    </Form>
  );
};
